import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTranslation } from "react-i18next";

interface CancelUploadDialogProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  uploadProgress: number;
  fileName?: string;
  fileSize?: number;
  templateName?: string;
}

export const CancelUploadDialog = ({
  isOpen,
  onCancel,
  onConfirm,
  uploadProgress,
  fileName,
  fileSize,
  templateName,
}: CancelUploadDialogProps) => {
  const { t } = useTranslation();

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('common.cancel_upload_title')}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              {fileName && (
                <div className="p-3 bg-muted rounded-lg overflow-hidden">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-brand-100 rounded flex items-center justify-center flex-shrink-0">
                      <span className="text-brand-600 text-xs font-medium">
                        {fileName.split(".").pop()?.toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1 overflow-hidden">
                      <p className="font-medium text-foreground text-sm truncate" title={fileName}>
                        {fileName}
                      </p>
                      {fileSize && (
                        <p className="text-xs text-muted-foreground">
                          {(fileSize / (1024 * 1024)).toFixed(1)} {t('common.mb')}
                        </p>
                      )}
                    </div>
                  </div>
                  {templateName && (
                    <div className="mt-2 pt-2 border-t border-border">
                      <p className="text-xs text-muted-foreground">
                        {t('common.template_label')} <span className="font-medium">{templateName}</span>
                      </p>
                    </div>
                  )}
                </div>
              )}
              <p className="text-sm text-muted-foreground">
                {t('common.cancel_upload_message_progress', { progress: Math.round(uploadProgress) })}
              </p>
              <p className="font-medium text-sm text-foreground">
                {t('common.cancel_upload_confirm_question')}
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel onClick={onCancel} className="order-2 sm:order-1">
            {t('common.continue_upload')}
          </AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="order-1 sm:order-2 bg-red-600 hover:bg-red-700">
            {t('common.confirm_cancel_upload')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
